import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Avatar,
    Button,
    Card,
    CardActions,
    CardContent,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Skeleton,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ActionResponseStatusAi } from '@/ai/enum/action-response-status-ai';
import _ from 'lodash';
import dayjs from 'dayjs';
import PostTypeIcon from '@/components/post-planning/post/PostTypeIcon';
import { GridExpandMoreIcon } from '@mui/x-data-grid';
import colors from '@/colors-palette';
import { PostType } from '@/graphql/generated/types.generated';
import { ActionRenderAi } from '@/ai/types/action-render-ai';
import { ActionWorkflowAi } from '@/ai/types/action-workflow-ai';
import { CloseRounded } from '@mui/icons-material';
import { useState } from 'react';
import Markdown from 'react-markdown';

const markdownLinkComponents = {
    a: ({ children, href, ...props }: React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
        <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
            {children}
        </a>
    ),
};

type DateTopic = {
    topic: string;
    dateTime: string;
    topicJustification: string;
    postTypeString: PostType;
    dateJustification: string;
    postTypeJustification: string;
};

export const AndiePlanningIdeasConfirmation = ({
    status,
    result,
}: ActionRenderAi<
    ActionWorkflowAi<{
        dates: DateTopic[];
    }>,
    {
        userInput: string;
        planningIntervalDays: string;
    }
>) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const results = result?.result;
    const [previewModal, setPreviewModal] = useState(false);
    // `down('xs')` is `@media (max-width:-0.05px)` in MUI v5+, i.e. it never matches, so the
    // dialog never actually went fullScreen on a phone. `sm` is the phone breakpoint.
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const isLoading =
        status === ActionResponseStatusAi.IN_PROGRESS || status === ActionResponseStatusAi.EXECUTING;

    if (isLoading) {
        return (
            <CardContent
                sx={{
                    p: 1,
                    '&:last-child': {
                        pb: 1,
                    },
                }}
            >
                <Stack gap={2}>
                    {_.times(3).map((i) => (
                        <Accordion
                            variant="outlined"
                            elevation={0}
                            disableGutters
                            key={i}
                            sx={{
                                width: '100%',
                                borderRadius: 2,
                                ':first-of-type': {
                                    borderRadius: 2,
                                },
                                ':last-of-type': {
                                    borderRadius: 2,
                                },
                                ':before': {
                                    height: 0,
                                },
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<></>}
                                sx={{
                                    pointerEvents: 'none',
                                    '& .MuiAccordionSummary-content': { minWidth: 0 },
                                }}
                            >
                                <Stack direction={'row'} gap={2} width={'100%'} alignItems={'center'}>
                                    <Stack justifyContent={'center'}>
                                        <Skeleton variant="circular" width={40} height={40} />
                                    </Stack>
                                    <Stack minWidth={0} width={'100%'} gap={0.5}>
                                        <Skeleton
                                            variant="rounded"
                                            width="100%"
                                            height={theme.typography.body1.fontSize}
                                        />
                                        <Skeleton
                                            variant="rounded"
                                            width="50%"
                                            height={theme.typography.caption.fontSize}
                                        />
                                    </Stack>
                                </Stack>
                            </AccordionSummary>
                        </Accordion>
                    ))}
                </Stack>
            </CardContent>
        );
    }

    if (!Array.isArray(results?.dates)) return null;

    return (
        <>
            <CardContent
                sx={{
                    p: 1,
                    '&:last-child': {
                        pb: 1,
                    },
                }}
            >
                {results?.dates && Array.isArray(results.dates) && results.dates.length > 0 ? (
                    <Stack gap={2}>
                        {[...results.dates]
                            .sort(
                                (a: { dateTime: string }, b: { dateTime: string }) =>
                                    dayjs(a.dateTime).toDate().getTime() -
                                    dayjs(b.dateTime).toDate().getTime(),
                            )
                            .slice(0, 7)
                            .map(
                                (
                                    postIdea: {
                                        topic: string;
                                        dateTime: string;
                                        topicJustification: string;
                                        postTypeString: PostType;
                                        dateJustification: string;
                                        postTypeJustification: string;
                                    },
                                    index: number,
                                ) => (
                                    <Accordion
                                        variant="outlined"
                                        elevation={0}
                                        disableGutters
                                        key={index}
                                        sx={{
                                            width: '100%',
                                            borderRadius: 2,
                                            ':first-of-type': {
                                                borderRadius: 2,
                                            },
                                            ':last-of-type': {
                                                borderRadius: 2,
                                            },
                                            ':before': {
                                                height: 0,
                                            },
                                        }}
                                    >
                                        <AccordionSummary
                                            expandIcon={<GridExpandMoreIcon />}
                                            sx={{ '& .MuiAccordionSummary-content': { minWidth: 0 } }}
                                        >
                                            <Stack
                                                direction={'row'}
                                                gap={2}
                                                width={'100%'}
                                                alignItems={'center'}
                                            >
                                                <Stack justifyContent={'center'}>
                                                    <Avatar
                                                        sx={{
                                                            justifyContent: 'center',
                                                            alignItems: 'center',
                                                            display: 'flex',
                                                            bgcolor: colors.blue[100],
                                                            color: theme.palette.primary.main,
                                                        }}
                                                    >
                                                        <PostTypeIcon
                                                            sx={{ color: theme.palette.primary.main }}
                                                            type={postIdea.postTypeString}
                                                            fontSize="medium"
                                                        />
                                                    </Avatar>
                                                </Stack>
                                                <Stack minWidth={0}>
                                                    <Typography variant="body1" noWrap color="text.primary">
                                                        {postIdea.topic}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        noWrap
                                                        color="text.secondary"
                                                    >
                                                        {dayjs(postIdea.dateTime).format('L LT')}
                                                    </Typography>
                                                </Stack>
                                            </Stack>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Stack gap={2}>
                                                <Stack gap={1}>
                                                    <Typography variant="body1" color="text.primary">
                                                        {t(
                                                            'andie.tool.generate-planning.topic-justification',
                                                        )}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        component={'span'}
                                                    >
                                                        <Markdown components={markdownLinkComponents}>
                                                            {postIdea.topicJustification}
                                                        </Markdown>
                                                    </Typography>
                                                </Stack>
                                                <Stack gap={1}>
                                                    <Typography variant="body1" color="text.primary">
                                                        {t('andie.tool.generate-planning.date-justification')}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        component={'span'}
                                                    >
                                                        <Markdown components={markdownLinkComponents}>
                                                            {postIdea.dateJustification}
                                                        </Markdown>
                                                    </Typography>
                                                </Stack>
                                                <Stack gap={1}>
                                                    <Typography variant="body1" color="text.primary">
                                                        {t(
                                                            'andie.tool.generate-planning.post-type-justification',
                                                        )}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        component={'span'}
                                                    >
                                                        <Markdown components={markdownLinkComponents}>
                                                            {postIdea.postTypeJustification}
                                                        </Markdown>
                                                    </Typography>
                                                </Stack>
                                            </Stack>
                                        </AccordionDetails>
                                    </Accordion>
                                ),
                            )}
                    </Stack>
                ) : (
                    <Stack>{t('andie.chat.no-planning-ideas')}</Stack>
                )}
            </CardContent>
            {results?.dates && Array.isArray(results.dates) && results.dates.length > 7 && (
                <>
                    <Divider />
                    <CardActions sx={{ justifyContent: 'center' }}>
                        <Button variant="text" size="small" onClick={() => setPreviewModal(true)}>
                            {t('andie.tool.generate-planning.see-more', { value: results.dates.length - 7 })}
                        </Button>
                    </CardActions>
                    <Dialog
                        open={previewModal}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            '& .MuiDialog-container': {
                                width: '100%',
                            },
                        }}
                        onClose={() => setPreviewModal(false)}
                        fullScreen={fullScreen}
                    >
                        <DialogTitle>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography variant="h4">
                                    {t('andie.tool.generate-planning.modal.title')}
                                </Typography>
                                <IconButton onClick={() => setPreviewModal(false)}>
                                    <CloseRounded />
                                </IconButton>
                            </Stack>
                        </DialogTitle>
                        <DialogContent dividers>
                            <Stack width={'100%'} gap={2}>
                                {Object.entries(
                                    results.dates.reduce<
                                        Record<
                                            string,
                                            {
                                                startDate: string;
                                                endDate: string;
                                                posts: DateTopic[];
                                                index: number;
                                            }
                                        >
                                    >((acc, postIdea) => {
                                        const datePost = dayjs(postIdea.dateTime);
                                        const weekNumber = datePost.week();
                                        if (!acc[weekNumber]) {
                                            const startOfWeek = datePost.startOf('week');
                                            const endOfWeek = datePost.endOf('week');

                                            acc[weekNumber] = {
                                                startDate: startOfWeek.format('LL'),
                                                endDate: endOfWeek.format('LL'),
                                                posts: [],
                                                index: Object.keys(acc).length,
                                            };
                                        }
                                        acc[weekNumber].posts.push(postIdea);
                                        return acc;
                                    }, {}),
                                ).map(([week, { startDate, endDate, posts, index }]) => (
                                    <Accordion
                                        variant="outlined"
                                        elevation={0}
                                        disableGutters
                                        key={week}
                                        sx={{
                                            width: '100%',
                                            borderRadius: 2,
                                            ':first-of-type': {
                                                borderRadius: 2,
                                            },
                                            ':last-of-type': {
                                                borderRadius: 2,
                                            },
                                            ':before': {
                                                height: 0,
                                            },
                                        }}
                                        defaultExpanded={index === 0}
                                    >
                                        <AccordionSummary
                                            expandIcon={<GridExpandMoreIcon />}
                                            sx={{
                                                '& .MuiAccordionSummary-content': { minWidth: 0 },
                                            }}
                                        >
                                            <Stack
                                                direction={{ xs: 'column', sm: 'row' }}
                                                gap={{ xs: 1, sm: 2 }}
                                                width={'100%'}
                                                alignItems={'center'}
                                            >
                                                <Typography variant="h6">
                                                    {t('andie.tool.generate-planning.week', {
                                                        start: startDate,
                                                        end: endDate,
                                                    })}
                                                </Typography>
                                            </Stack>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Stack spacing={2}>
                                                {posts.map((postIdea, index) => (
                                                    <Card
                                                        key={index}
                                                        variant="outlined"
                                                        sx={{ width: '100%' }}
                                                    >
                                                        <CardContent>
                                                            <Stack gap={2} width={'100%'}>
                                                                <Stack
                                                                    direction={'row'}
                                                                    gap={2}
                                                                    width={'100%'}
                                                                    alignItems={'center'}
                                                                >
                                                                    <Stack justifyContent={'center'}>
                                                                        <Avatar
                                                                            sx={{
                                                                                justifyContent: 'center',
                                                                                alignItems: 'center',
                                                                                display: 'flex',
                                                                                bgcolor: colors.blue[100],
                                                                                color: theme.palette.primary
                                                                                    .main,
                                                                            }}
                                                                        >
                                                                            <PostTypeIcon
                                                                                sx={{
                                                                                    color: theme.palette
                                                                                        .primary.main,
                                                                                }}
                                                                                type={postIdea.postTypeString}
                                                                                fontSize="medium"
                                                                            />
                                                                        </Avatar>
                                                                    </Stack>
                                                                    <Stack minWidth={0}>
                                                                        <Typography
                                                                            variant="body1"
                                                                            noWrap
                                                                            color="text.primary"
                                                                        >
                                                                            {postIdea.topic}
                                                                        </Typography>
                                                                        <Typography
                                                                            variant="caption"
                                                                            noWrap
                                                                            color="text.secondary"
                                                                        >
                                                                            {dayjs(postIdea.dateTime).format(
                                                                                'L LT',
                                                                            )}
                                                                        </Typography>
                                                                    </Stack>
                                                                </Stack>
                                                                <Stack width={'100%'} gap={1}>
                                                                    <Typography
                                                                        variant="subtitle2"
                                                                        color="text.primary"
                                                                    >
                                                                        {t(
                                                                            'andie.tool.generate-planning.topic-justification',
                                                                        )}
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="body2"
                                                                        color="text.secondary"
                                                                        component={'span'}
                                                                    >
                                                                        <Markdown components={markdownLinkComponents}>
                                                                            {postIdea.topicJustification}
                                                                        </Markdown>
                                                                    </Typography>
                                                                </Stack>
                                                                <Stack gap={1}>
                                                                    <Typography
                                                                        variant="subtitle2"
                                                                        color="text.primary"
                                                                    >
                                                                        {t(
                                                                            'andie.tool.generate-planning.date-justification',
                                                                        )}
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="body2"
                                                                        color="text.secondary"
                                                                        component={'span'}
                                                                    >
                                                                        <Markdown components={markdownLinkComponents}>
                                                                            {postIdea.dateJustification}
                                                                        </Markdown>
                                                                    </Typography>
                                                                </Stack>
                                                                <Stack gap={1}>
                                                                    <Typography
                                                                        variant="subtitle2"
                                                                        color="text.primary"
                                                                    >
                                                                        {t(
                                                                            'andie.tool.generate-planning.post-type-justification',
                                                                        )}
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="body2"
                                                                        color="text.secondary"
                                                                        component={'span'}
                                                                    >
                                                                        <Markdown components={markdownLinkComponents}>
                                                                            {postIdea.postTypeJustification}
                                                                        </Markdown>
                                                                    </Typography>
                                                                </Stack>
                                                            </Stack>
                                                        </CardContent>
                                                    </Card>
                                                ))}
                                            </Stack>
                                        </AccordionDetails>
                                    </Accordion>
                                ))}
                            </Stack>
                        </DialogContent>
                    </Dialog>
                </>
            )}
        </>
    );
};
